import React, { useEffect, useRef, useState } from "react";

import { IChartTAAData, IRootReducer } from "../../../Models/Models";
import {
    ResponsiveContainer,
    ComposedChart,
    Line,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Legend,
    Label,
    ReferenceLine,
    ReferenceDot,
} from "recharts";
import { Select } from "antd";
import { Button } from "../Button/Button";
import { ReportDataWrapper } from "../ReportStyles/ReportWrappers";
import { ChartTitle } from "./ChartTitle";
import { ChartBodyWrapper } from "../ChartStyles/ChartWrapper";
import { shallowEqual, useSelector } from "react-redux";
import { BodyText } from "../ReportStyles/Text";
import {
    colorTwoBright,
    colorTwoDark,
} from "../../../globalStyles/colors/scoutColors";
import { ChartContainer } from "./ChartContainer";
import {
    TAALegendItemDot,
    TAAChartLegend,
    TAAChartLendItem,
    TAAOptionsContainer,
    TAAOptions,
} from "../ChartStyles/TAAStyles";
import { formatLabelWithEllipsis } from "../SharedActions";
import { featureFlags } from "../../../Common/CurrentFeatureFlags";
import { IIdentifier } from "../../../Models/IIdentifier";
import { getPolygonIdentifiers } from "../../Apps/GPSInsights/Common";
import { PolygonNameWithCircle } from "../";
import "../Select.css";
import { RenderType } from "../../../Enums/RenderType";
import { DigitalShareAppType } from "../../../Enums/DigitalShareAppType";
import { DigitalShareItemType } from "../../../Enums/DigitalShareItemType";
import {
    CHILD_HEIGHT,
    isOutputSelected,
    registerElement,
    registerRef,
} from "../DigitalShare/DigitalShareGenerator";
import MobilyticsPolygonSelect from "../../Apps/GPSInsights/MobilyticsPolygonSelect";
import { IMobilyticsBenchmarkItem } from "../../../Models/IMobilyticsBenchmarkItem";
import { getPolygonNamesForSelectedGpsInsight } from "../../../Reducers/Selectors/ChartSelectors";

interface IConvertedChartData {
  id: string;
  visitWeight?: number;
  visitorWeight?: number;
  visitWeightCumulative?: number;
  visitorWeightCumulative?: number;
  visitWeight2?: number;
  visitorWeight2?: number;
  visitWeightCumulative2?: number;
  visitorWeightCumulative2?: number;
}

interface TAACumulativeValue {
  id: string;
  value: number;
}

interface IRechartTAAProps {
  data: IChartTAAData[][];
  renderType?: RenderType;
  app?: DigitalShareAppType;
  itemType?: DigitalShareItemType;
  benchmarkItems: IMobilyticsBenchmarkItem[];
}

enum TAAWeightToggleEnum {
  VISITORS = "visitorWeight",
  VISITS = "visitWeight",
}

enum TAAYAxisLabelEnum {
  VISITORS = "% of total Visitors",
  VISITS = "% of total Visits",
}

const LineSVG = (props) => {
    return (
        <svg
            width="14px"
            height="7px"
            viewBox="0 0 14 7"
            version="1.1"
            style={{ marginRight: "5px" }}
        >
            <g
                id="Page-1"
                stroke="none"
                strokeWidth="1"
                fill="none"
                fillRule="evenodd"
            >
                <path
                    d="M0,6.5 L2,6.5 L2,0.5 L0,0.5 L0,6.5 Z M4,6.5 L6,6.5 L6,0.5 L4,0.5 L4,6.5 Z M8,6.5 L10,6.5 L10,0.5 L8,0.5 L8,6.5 Z M12,6.5 L14,6.5 L14,0.5 L12,0.5 L12,6.5 Z"
                    id="Path-7"
                    fill={props.fill}
                    fillRule="nonzero"
                />
            </g>
        </svg>
    );
};

const RechartTAA = (props: IRechartTAAProps): JSX.Element => {
    const [chartData, setChartData] = useState<IConvertedChartData[]>([]);
    const [xAxisKey, setXAxisKey] = useState<TAAWeightToggleEnum>(
        TAAWeightToggleEnum.VISITORS
    );
    const [yAxisLabel, setYAxisLabel] = useState<string>(
        TAAYAxisLabelEnum.VISITORS
    );
    const [cumulativeValues, setCumulativeValues]
    = useState<
      Map<
        string,
        {
          visitorWeightCumulativeValues: TAACumulativeValue[];
          visitWeightCumulativeValues: TAACumulativeValue[];
        }
      >
    >();
    const [referenceY, setReferenceY] = useState<number>(0);
    const [referenceX, setReferenceX] = useState<string>("");
    const [locationReference, setLocationReference] = useState<IIdentifier>({
        name: "",
        identifier: "",
    });
    const [selectedPolygonIndex, setSelectedPolygonIndex] = useState<number>(0);

    const polygonNames = useSelector(getPolygonNamesForSelectedGpsInsight);

    const setCumulativeData = () => {
        const cd: IConvertedChartData[] = [];
        const cumulativeMap: Map<
      string,
      {
        visitorWeightCumulativeValues: TAACumulativeValue[];
        visitWeightCumulativeValues: TAACumulativeValue[];
      }
    > = new Map();
        const polygonIds: IIdentifier[] = getPolygonIdentifiers(polygonNames);

        let visitAccumulator = 0;
        let visitorAccumulator = 0;

        for (const d of props.data[0]) {
            const visitWeight: number = parseFloat(d.visitWeight) * 100;
            const visitorWeight: number = parseFloat(d.visitorWeight) * 100;

            const obj = {
                id: d.id,
                visitWeight: Math.floor(visitWeight),
                visitorWeight: Math.floor(visitorWeight),
                visitWeightCumulative: (visitAccumulator += visitWeight),
                visitorWeightCumulative: (visitorAccumulator += visitorWeight),
            };

            cd.push(obj);
        }

        cumulativeMap.set(polygonIds[0].identifier, {
            visitorWeightCumulativeValues: cd.map((d) => ({
                id: d.id,
                value: d.visitorWeightCumulative,
            })),
            visitWeightCumulativeValues: cd.map((d) => ({
                id: d.id,
                value: d.visitWeightCumulative,
            })),
        });

        if (props.data.length > 1) {
            const dataMap: Map<string, IConvertedChartData> = new Map();
            cd.forEach((d) => dataMap.set(d.id, d));

            visitAccumulator = 0;
            visitorAccumulator = 0;

            const newData: IConvertedChartData[] = [];

            for (const d of props.data[1]) {
                const visitWeight2: number = parseFloat(d.visitWeight) * 100;
                const visitorWeight2: number = parseFloat(d.visitorWeight) * 100;

                const obj = {
                    id: d.id,
                    visitWeight2: Math.floor(visitWeight2),
                    visitorWeight2: Math.floor(visitorWeight2),
                    visitWeightCumulative2: (visitAccumulator += visitWeight2),
                    visitorWeightCumulative2: (visitorAccumulator += visitorWeight2),
                };

                if (dataMap.has(d.id)) {
                    Object.assign(dataMap.get(d.id), obj);
                } else {
                    dataMap.set(d.id, obj);
                    cd.push(obj);
                }

                newData.push(obj);
            }

            cumulativeMap.set(polygonIds[1].identifier, {
                visitorWeightCumulativeValues: newData.map((d) => ({
                    id: d.id,
                    value: d.visitorWeightCumulative2,
                })),
                visitWeightCumulativeValues: newData.map((d) => ({
                    id: d.id,
                    value: d.visitWeightCumulative2,
                })),
            });
        }

        const { visitorWeightCumulativeValues } = cumulativeMap.get(
            polygonIds[0].identifier
        );

        setCumulativeValues(cumulativeMap);
        setReferenceX(
            visitorWeightCumulativeValues[
                Math.floor(visitorWeightCumulativeValues.length / 2)
            ]?.id
        );
        setReferenceY(
            visitorWeightCumulativeValues[
                Math.floor(visitorWeightCumulativeValues.length / 2)
            ]?.value
        );
        setLocationReference(polygonIds[0]);

        cd.sort((a, b) => parseInt(a.id) - parseInt(b.id));
        setChartData(cd.filter((i) => parseInt(i.id) < 41));
    };

    useEffect(setCumulativeData, []);
    for (const item of props.benchmarkItems) {
        useEffect(setCumulativeData, [item.name]);
    }

    useEffect(() => {
    //The selected item in the polygon select is a static string, and not an index that dynamically references an array
    //We have to reset it's value any time the names change
        const splitString = locationReference.identifier.split("-");
        if (splitString.length > 0) {
            const indexStr = splitString[splitString.length - 1];
            const index = Number(indexStr);
            setSelectedPolygonIndex(index);
        }
    }, [polygonNames]);

    const ref = useRef(null);

    const handleYAxisToggle = (value: TAAWeightToggleEnum): void => {
        const v = cumulativeValues.get(locationReference.identifier);
        const k = value.concat("CumulativeValues");

        switch (value) {
            case TAAWeightToggleEnum.VISITS: {
                setXAxisKey(TAAWeightToggleEnum.VISITS);
                setYAxisLabel(TAAYAxisLabelEnum.VISITS);
                break;
            }
            default:
                setXAxisKey(TAAWeightToggleEnum.VISITORS);
                setYAxisLabel(TAAYAxisLabelEnum.VISITORS);
                break;
        }

        setReferenceY(v[k].find((i) => i.id === referenceX).value);
    };

    const handleSetPercent = (sign: string): void => {
        const reverenceValues = cumulativeValues.get(locationReference.identifier);
        const cValues = reverenceValues[xAxisKey.concat("CumulativeValues")];
        let nextIndex = 0;

        cValues.forEach((v: TAACumulativeValue, i: number) => {
            if (v.id === referenceX && v.value === referenceY) {
                nextIndex = sign == "+" ? i + 1 : i - 1;
            }
        });

        if (nextIndex < cValues.length && nextIndex > -1) {
            setReferenceY(cValues[nextIndex].value);
            setReferenceX(cValues[nextIndex].id);
        }
    };

    const handleSetLocationReference = (value: string) => {
        const index = Number(value);
        const location = polygonIdentifiers[index];
        const v = cumulativeValues.get(location.identifier);
        const k = xAxisKey.concat("CumulativeValues");

        setLocationReference(location);
        setSelectedPolygonIndex(index);

        let ry: number = v[k].find((i) => i.id === referenceX)?.value;

        if (!ry) {
            ry = v[k][Math.floor(v[k].length / 2)]?.value;
            setReferenceX(v[k][Math.floor(v[k].length / 2)]?.id);
        }

        setReferenceY(ry);
    };

    const renderReferenceDotLabel = (props) => {
        const { viewBox, value } = props;
        const reference = Math.floor(value);

        return (
            <g>
                <rect
                    x={reference < 90 ? viewBox.x - 14 : viewBox.x - 30}
                    y={reference < 90 ? viewBox.y - 34 : viewBox.y + 15}
                    width="40"
                    height="30"
                    fill="#FFFFFF"
                    strokeWidth={1}
                    stroke="rgba(0, 0, 0, 0.20)"
                    rx="2"
                ></rect>
                <text
                    fill="#000"
                    x={reference < 90 ? viewBox.x - 6 : viewBox.x - 24}
                    y={reference < 90 ? viewBox.y - 15 : viewBox.y + 35}
                >
                    {reference.toString()}%
                </text>
            </g>
        );
    };

    const polygonIdentifiers: IIdentifier[] = getPolygonIdentifiers(polygonNames);

    const chart = (
        <ChartContainer
            chartHeight={props.renderType == RenderType.DIGITAL_SHARE && CHILD_HEIGHT}
        >
            <ResponsiveContainer>
                <ComposedChart
                    data={chartData}
                    barSize={100}
                    barGap={0}
                    height={468}
                    width={1288}
                >
                    <CartesianGrid stroke="lightgray" vertical={false} />
                    <YAxis
                        dataKey={xAxisKey.concat("Cumulative")}
                        tickFormatter={(v) => Math.floor(v)}
                        tick={{ fontSize: 12 }}
                        type="number"
                        domain={[0, (dataMax) => Math.min(dataMax + 5, 100.1)]}
                        tickCount={11}
                        axisLine={false}
                        tickLine={false}
                    >
                        <Label
                            value={yAxisLabel}
                            position="insideLeft"
                            angle={-90}
                            style={{ textAnchor: "middle" }}
                        />
                    </YAxis>
                    <XAxis
                        dataKey="id"
                        type="category"
                        tick={{ fontSize: 12 }}
                        domain={[0, "auto"]}
                        tickLine={false}
                        height={75}
                    >
                        <Label value={"Miles"} />
                    </XAxis>
                    <Bar dataKey={xAxisKey} fill={colorTwoBright} />
                    {props.data.length > 1 && (
                        <Bar dataKey={xAxisKey.concat("2")} fill={colorTwoDark} />
                    )}
                    <Line
                        type="monotone"
                        dataKey={xAxisKey.concat("Cumulative")}
                        stroke={colorTwoBright}
                        strokeDasharray="5 5"
                        strokeWidth={2}
                        dot={false}
                    />
                    {props.data.length > 1 && (
                        <Line
                            type="monotone"
                            dataKey={xAxisKey.concat("Cumulative2")}
                            stroke={colorTwoDark}
                            strokeDasharray="5 5"
                            strokeWidth={2}
                            dot={false}
                        />
                    )}
                    <Legend
                        content={
                            <>
                                {polygonIdentifiers.length > 0 && (
                                    <TAAChartLegend>
                                        <TAAChartLendItem
                                            key={`item-${polygonIdentifiers[0].identifier}-bar`}
                                        >
                                            <TAALegendItemDot color={colorTwoBright} />
                                            {formatLabelWithEllipsis(polygonIdentifiers[0].name, 15)}
                                        </TAAChartLendItem>
                                        {polygonIdentifiers[1] && (
                                            <TAAChartLendItem
                                                key={`item-${polygonIdentifiers[1].identifier}-bar`}
                                            >
                                                <TAALegendItemDot color={colorTwoDark} />
                                                {formatLabelWithEllipsis(
                                                    polygonIdentifiers[1].name,
                                                    15
                                                )}
                                            </TAAChartLendItem>
                                        )}
                                        <TAAChartLendItem
                                            key={`item-${polygonIdentifiers[0].identifier}-cumulative`}
                                        >
                                            <LineSVG fill={colorTwoBright} />
                                            {`Cumulative Capture - ${formatLabelWithEllipsis(
                                                polygonIdentifiers[0].name,
                                                15
                                            )}`}
                                        </TAAChartLendItem>
                                        {polygonIdentifiers[1] && (
                                            <TAAChartLendItem
                                                key={`item-${polygonIdentifiers[1].identifier}-cumulative`}
                                            >
                                                <LineSVG fill={colorTwoDark} />
                                                {`Cumulative Capture - ${formatLabelWithEllipsis(
                                                    polygonIdentifiers[1].name,
                                                    15
                                                )}`}
                                            </TAAChartLendItem>
                                        )}
                                    </TAAChartLegend>
                                )}
                            </>
                        }
                    />
                    <ReferenceLine
                        y={referenceY}
                        stroke={selectedPolygonIndex === 0 ? colorTwoBright : colorTwoDark}
                        isFront={true}
                    />
                    <ReferenceLine
                        x={referenceX}
                        stroke={selectedPolygonIndex === 0 ? colorTwoBright : colorTwoDark}
                        isFront={true}
                    />
                    <ReferenceDot
                        y={referenceY}
                        x={referenceX}
                        r={5}
                        fill={selectedPolygonIndex === 0 ? colorTwoBright : colorTwoDark}
                    >
                        <Label value={referenceY} content={renderReferenceDotLabel} />
                    </ReferenceDot>
                </ComposedChart>
            </ResponsiveContainer>
        </ChartContainer>
    );

    const getChartText = (): string => {
        const selection
      = xAxisKey == TAAWeightToggleEnum.VISITORS ? "visitors" : "visits";
        return `
    Shows the distance in miles from where ${selection} to ${polygonNames[0]}${
    polygonNames?.length < 2 ? "." : ""
}
    ${
    polygonNames?.length > 1
        ? " and " + polygonNames[1] + " originated (home location)."
        : ""
}`;
    };

    if (
        featureFlags.DigitalShare
    && props.renderType === RenderType.DIGITAL_SHARE
    && isOutputSelected(props.app, props.itemType)
    ) {
        registerRef(props.app.toString(), props.itemType.toString(), ref);

        registerElement(
            props.app.toString(),
            props.itemType.toString(),
            getChartText(),
            chart
        );
    }
    return (
        <ReportDataWrapper id="rechart-print">
            <ChartTitle titleText={"Visitor Trade Area"} tooltipText={""} />
            <BodyText>
                <span>{getChartText()}</span>
            </BodyText>
            <div id={"rechart-print-body-outer"}>
                <div id={"rechart-print-body"}>
                    <ChartBodyWrapper
                        scrollableParentSelector={"#GPSReportBody"}
                        animateIn={"zoomIn"}
                        offset={100}
                        width={"initial"}
                        animateOnce={true}
                    >
                        <TAAOptionsContainer>
                            <TAAOptions>
                                <div style={{ display: "flex", alignItems: "center" }}>
                                    <Button
                                        title="-"
                                        clickCallback={() => handleSetPercent("-")}
                                        style={{
                                            width: "31px",
                                            height: "31px",
                                            padding: "0 11px 3px",
                                            borderRadius: "4px",
                                            border: "solid 1px #d9d9d9",
                                            backgroundColor: "#ffffff",
                                        }}
                                    />
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            width: "61px",
                                            height: "31px",
                                            padding: "5px 10px 5px 18px",
                                            borderRadius: "4px",
                                            border: "solid 1px #d9d9d9",
                                            backgroundColor: "#ffffff",
                                        }}
                                    >
                                        {Math.floor(referenceY)}%
                                    </div>
                                    <Button
                                        title="+"
                                        clickCallback={() => handleSetPercent("+")}
                                        style={{
                                            width: "31px",
                                            height: "31px",
                                            padding: "0 11px 3px",
                                            borderRadius: "4px",
                                            border: "solid 1px #d9d9d9",
                                            backgroundColor: "#ffffff",
                                        }}
                                    />
                                </div>
                of
                                <div style={{ display: "flex" }}>
                                    <div style={{ marginRight: 10 }}>
                                        <MobilyticsPolygonSelect
                                            benchmarkItems={props.benchmarkItems}
                                            selectedIndex={selectedPolygonIndex}
                                            onChange={handleSetLocationReference}
                                        />
                                    </div>
                                    <Select
                                        defaultValue={xAxisKey}
                                        value={xAxisKey}
                                        onChange={handleYAxisToggle}
                                    >
                                        <Select.Option value={TAAWeightToggleEnum.VISITORS}>
                      Visitors
                                        </Select.Option>
                                        <Select.Option value={TAAWeightToggleEnum.VISITS}>
                      Visits
                                        </Select.Option>
                                    </Select>
                                </div>
                                <div>
                  are within <strong> {referenceX} mile(s)</strong> of location.
                                </div>
                            </TAAOptions>
                        </TAAOptionsContainer>
                        {chart}
                    </ChartBodyWrapper>
                </div>
            </div>
        </ReportDataWrapper>
    );
};

export default RechartTAA;
